import { Controller } from "@hotwired/stimulus"
import loadYmaps from "../../src/shared/helpers/load_ymaps"

export default class extends Controller {
  static targets = ["map"]
  static values = { cities: String, regionName: String }

  connect() {
    console.log("Cities controller connected")
    console.log("Region name:", this.regionNameValue)

    try {
      if (!this.citiesValue || this.citiesValue === 'null' || this.citiesValue === 'undefined') {
        console.error("No valid cities data provided")
        this.parsedCities = []
      } else {
        // Try to parse the JSON
        this.parsedCities = JSON.parse(this.citiesValue)
        console.log("Parsed cities:", this.parsedCities.length, "cities")
      }

      this.initializeMap()
    } catch (error) {
      console.error("Error parsing cities data:", error)
      this.parsedCities = []
      this.initializeMap()
    }
  }

  initializeMap() {
    loadYmaps(() => {
      ymaps.ready(() => {
        // Determine map center and zoom based on cities
        const mapConfig = this.getMapConfig()

        const map = new ymaps.Map(this.element, {
          center: mapConfig.center,
          zoom: mapConfig.zoom,
          controls: ['zoomControl', 'fullscreenControl']
        })

        if (this.parsedCities && this.parsedCities.length > 0) {
          this.addCitiesToMap(map)
        } else {
          console.log("No cities to display on map")
        }
      })
    })
  }

  getMapConfig() {
    if (this.parsedCities.length === 0) {
      // Default center (Moscow)
      return {
        center: [55.76, 37.64],
        zoom: 5
      }
    }

    // Calculate center based on all cities
    const totalLat = this.parsedCities.reduce((sum, city) => sum + city.lat, 0)
    const totalLng = this.parsedCities.reduce((sum, city) => sum + city.lng, 0)

    return {
      center: [totalLat / this.parsedCities.length, totalLng / this.parsedCities.length],
      zoom: 8 // Will be adjusted by setBounds
    }
  }

  addCitiesToMap(map) {
    const allPlacemarks = []

    this.parsedCities.forEach(city => {
      // Create placemark for each city
      const cityPlacemark = new ymaps.Placemark(
        [city.lat, city.lng],
        {
          balloonContentHeader: `${city.name}`,
          balloonContentBody: this.getCityBalloonContent(city),
          hintContent: `${city.name} (${city.tech_placements_count} размещений)`
        },
        {
          preset: this.getCityPreset(city),
          iconColor: this.getCityIconColor(city),
          iconContent: city.tech_placements_count > 99 ? '99+' : city.tech_placements_count.toString()
        }
      )
      allPlacemarks.push(cityPlacemark)

      // Add each placemark directly to the map
      map.geoObjects.add(cityPlacemark)
    })

    // Fit map to show all cities
    if (allPlacemarks.length > 0) {
      // Calculate bounds manually from all placemarks
      const coordinates = this.parsedCities.map(city => [city.lat, city.lng])
      const bounds = [
        [Math.min(...coordinates.map(c => c[0])), Math.min(...coordinates.map(c => c[1]))],
        [Math.max(...coordinates.map(c => c[0])), Math.max(...coordinates.map(c => c[1]))]
      ]

      map.setBounds(bounds, {
        checkZoomRange: true,
        zoomMargin: 50
      })
    }

    console.log(`Added ${allPlacemarks.length} city placemarks`)
  }

  getCityPreset(city) {
    if (city.capital) {
      return 'islands#redDotIcon'
    } else if (city.tech_placements_count >= 10) {
      return 'islands#blueDotIcon'
    } else {
      return 'islands#greenDotIcon'
    }
  }

  getCityIconColor(city) {
    if (city.capital) {
      return '#ff5722' // Red for capitals
    } else if (city.tech_placements_count >= 10) {
      return '#1e88e5' // Blue for cities with many placements
    } else {
      return '#4caf50' // Green for cities with few placements
    }
  }

  getCityBalloonContent(city) {
    const areaInfo = city.region_area_name ?
      `Район: <strong>${city.region_area_name}</strong><br>` : ''

    return `
      ${areaInfo}
      Кол-во размещений: <strong>${city.tech_placements_count}</strong><br>
      ${city.capital ? '<strong>Статус:</strong> Столица региона<br>' : ''}
      Ранг цены: <strong>${city.price_rank} (${this.getPriceRankName(city.price_rank)})</strong><br>
      <br>
      <a href="/admin/cities/${city.id}/edit" class="btn btn-success btn-sm">
        <i class="bi bi-pencil"></i> Редактировать
      </a>
    `
  }

  getPriceRankName(priceRank) {
    const priceRanks = {
      1: 'Очень низкая',
      2: 'Низкая',
      3: 'Средняя',
      4: 'Высокая',
      5: 'Очень высокая'
    }
    return priceRanks[priceRank] || 'Неизвестно'
  }
}
